import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// Services
import { authService } from '../services/auth.service';
import { showAlert } from '../utils/toast';

// Types
import { AuthContextType, AuthState, LoginCredentials } from '../types/auth';

/**
 * Kiểm tra token trong localStorage có hợp lệ không
 * @returns boolean - true nếu token hợp lệ và chưa hết hạn
 */
const hasValidStoredToken = (): boolean => {
  try {
    const storedToken = localStorage.getItem('@USER_TOKEN');
    if (!storedToken) return false;
    
    const tokenData = JSON.parse(storedToken);
    if (!tokenData.access_token) return false;
    
    // Kiểm tra token hết hạn
    const tokenExpires = moment(tokenData['.expires']);
    return !moment().isAfter(tokenExpires); // True nếu token chưa hết hạn
  } catch (error) {
    console.error('Error checking stored token:', error);
    return false;
  }
};

/**
 * Trạng thái khởi tạo cho context
 */
const initialState: AuthState = {
  isAuthenticated: hasValidStoredToken(),
  isLoading: true, // Ngăn hiển thị login screen ngay lập tức
  isCheckingAuth: true,
};

// Ngăn các API calls trùng lặp khi chuyển tab
let isAuthInitialized = false;

// Tạo context với type đầy đủ
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Provider component để quản lý trạng thái xác thực trong ứng dụng
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(initialState);
  const navigate = useNavigate();

  // Kiểm tra xác thực khi component mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Listen for logout events from auth service
  useEffect(() => {
    const handleLogoutEvent = () => {
      // Reset flag to allow reinitializing auth
      isAuthInitialized = false;
      
      // Update React state
      setState({
        isAuthenticated: false,
        isLoading: false,
        isCheckingAuth: false
      });
      // Handle navigation (this can't be done through the event)
      navigate('/login');
    };

    // Add event listener
    window.addEventListener('vf_logout_event', handleLogoutEvent);

    // Cleanup
    return () => {
      window.removeEventListener('vf_logout_event', handleLogoutEvent);
    };
  }, []);

  /**
   * Kiểm tra trạng thái xác thực người dùng và cập nhật state
   */
  const checkAuth = async () => {
    console.log('Checking auth with initial state:', state);
    
    try {
      // Trực tiếp kiểm tra token từ localStorage thay vì GlobalApi
      const storedToken = localStorage.getItem('@USER_TOKEN');
      if (!storedToken) {
        console.log('No token found in localStorage, skipping auth check');
        setState({
          isAuthenticated: false,
          isLoading: false,
          isCheckingAuth: false
        });
        return;
      }
      
      // Parse token và kiểm tra hết hạn
      try {
        const tokenData = JSON.parse(storedToken);
        if (!tokenData.access_token) {
          throw new Error('Invalid token format');
        }
        
        // Kiểm tra token expiry
        const tokenExpires = moment(tokenData['.expires']);
        const tokenExpired = moment().isAfter(tokenExpires);
        
        if (tokenExpired) {
          console.log('Token expired, attempting to refresh');
          // Token đã hết hạn, thử refresh
          try {
            const rRes = await authService.refreshToken();
            if (!rRes) {
              throw new Error('Refresh token failed');
            }
            console.log('Token refreshed successfully');
          } catch (error) {
            console.error('Token refresh failed:', error);
            authService.logout();
            setState({
              isAuthenticated: false,
              isLoading: false,
              isCheckingAuth: false
            });
            return;
          }
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        authService.logout();
        setState({
          isAuthenticated: false,
          isLoading: false,
          isCheckingAuth: false
        });
        return;
      }
      
      // Nếu quá trình init đã hoàn tất, skip việc gọi API lại
      if (isAuthInitialized) {
        console.log('Auth already initialized, skipping API calls');
        setState({
          isAuthenticated: true,
          isLoading: false,
          isCheckingAuth: false
        });
        return;
      }

      // Kiểm tra nếu đã có links trong localStorage
      const cachedLinks = authService.getHomeLinks();
      if (cachedLinks) {
        console.log('Using cached links');
        // Kiểm tra và load dữ liệu ban đầu nếu chưa có
        await authService.checkAndLoadInitialData();
        setState({
          isAuthenticated: true,
          isLoading: false,
          isCheckingAuth: false
        });
        return;
      }
      
      // Nếu chưa có links, thử gọi API getLinks với retry
      let retryCount = 0;
      const maxRetries = 3;
      let links = null;

      while (retryCount < maxRetries && !links) {
        try {
          links = await authService.storeHomeLinks();
          if (links) {
            console.log('Successfully retrieved links from API');
            // Load initial data sau khi lấy links thành công
            await authService.loadInitialData();
            // Set flag để biết đã init xong, tránh gọi API nhiều lần
            isAuthInitialized = true;
            setState({
              isAuthenticated: true,
              isLoading: false,
              isCheckingAuth: false
            });
            return;
          }
        } catch (error) {
          console.error(`Get links attempt ${retryCount + 1} failed:`, error);
          retryCount++;
          if (retryCount < maxRetries) {
            // Đợi 1s trước khi thử lại
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      // Nếu đã thử hết số lần mà vẫn thất bại
      console.error('Get links failed after all retries');
      authService.setForceLogoutFlag();
      authService.logout();
      setState({
        isAuthenticated: false,
        isLoading: false,
        isCheckingAuth: false
      });
    } catch (error) {
      console.error('Check auth error:', error);
      authService.setForceLogoutFlag();
      authService.logout();
      setState({
        isAuthenticated: false,
        isLoading: false,
        isCheckingAuth: false
      });
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      // Đặt loading state để Login component biết đang xử lý
      setState(prev => ({ ...prev, isLoading: true }));

      // Thực hiện login
      await authService.login(credentials);
      
      // Fetch và store home links
      const links = await authService.storeHomeLinks();
      if (!links) {
        throw new Error('Failed to fetch home links');
      }

      // Subscribe to notifications
      await authService.subscribeToNotifications();
      
      // Load initial data after successful login, nhưng chỉ nếu chưa có dữ liệu cached
      await authService.checkAndLoadInitialData();
      console.log('Initial data loaded or verified after login');
      
      // Đánh dấu đã init auth xong để tránh gọi API lại nhiều lần
      isAuthInitialized = true;
      
      // Cập nhật trạng thái auth
      setState({
        isAuthenticated: true,
        isLoading: false,
        isCheckingAuth: false
      });

      // Hiển thị cảnh báo an toàn sau khi đăng nhập thành công
      sessionStorage.setItem('showSecurityWarningAfterLogin', 'true');
      
      // Chỉ chuyển hướng sau khi tất cả hoàn tất
      navigate('/assets');
    } catch (error) {
      // Clear auth state nếu có lỗi
      authService.logout();
      
      // Reset state
      setState({
        isAuthenticated: false,
        isLoading: false,
        isCheckingAuth: false
      });
      
      throw error;
    }
  };

  const logout = () => {
    // Just call the auth service's logout method
    // State updates will happen through the event listener
    authService.logout();
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook để truy cập AuthContext
 * @throws Error nếu sử dụng bên ngoài AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
